<script setup lang="ts">
/**
 * ReceiveMilkDialog
 *
 * Modal for receiving a new human milk container into inventory.
 * Used for both Mother and Donor milk tabs.
 *
 * Fields:
 *  - Container ID (barcode scan or manual entry) — mandatory, alphanumeric, unique
 *  - Origin Type (Mother / Donor) — pre-set from tab context
 *  - Mother / Donor Name — mandatory, free-text search against patient DB
 *  - Volume (ml) — mandatory, numeric > 0
 *  - Milk Type — dropdown (backend enum: Preterm / Term / Foremilk / Hindmilk / Skim Human Milk / Colostrum)
 *  - Milk Status — dropdown (Fresh / Frozen / Thawed)
 *  - Location — dropdown (hospital-seeded)
 *  - Pumped Date — date picker, mandatory, not in the future
 *  - Expiry Date — date picker, auto-calculated from Pumped Date + shelf-life rule
 *
 * Business rules:
 *  - Duplicate container ID → rejected server-side (409); parent surfaces the error
 *  - Pumped Date in the future → validation error
 *  - Expiry auto-calculation: driven by API rules (GET /settings/api/v1/milkExpiryRules)
 *  - Shelf-life hint displayed at the bottom (from hospital configuration)
 */
import { DialogModal, useToaster, Icon } from '@tms-phoenix/ui-vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import AppDatePicker from '@/shared/components/ui/AppDatePicker.vue';
import InputNumber from 'primevue/inputnumber';
import type {
  MilkReceivableMilkStatus,
  MilkReceivableMilkType,
  DonorApprovalStatus,
  ReceiveMilkContainerPayload,
} from '../types';
import { getMilkProviderByLabel } from '../services/milk-receivables.api';
import { config, API_ROUTES } from '@/core/config';
import { useLocale } from '@/shared/composables/useLocale';
import { useEnumTranslation } from '@/shared/composables/useEnumTranslation';
import { useFormDirty } from '@/shared/composables/useFormDirty';
import FormDirtyDialog from '@/shared/components/feedback/FormDirtyDialog.vue';
import { getApiClient } from '@/core/services/api.service';
import { useSettingsStore } from '@/stores/settings.store';
import type { MilkTypeEntry } from '@/modules/settings/types';
import { useBarcodeScanner } from '@/shared/composables/useBarcodeScanner';
import ScanInput from '@/shared/components/ui/ScanInput.vue';
import { formatLocationLabel, getMilkStorageTypeLabel } from '@/shared/utils/location-label';
import {
  getExpiryOffsetHoursForStatus,
  getExpiryRows,
  getLocationBucket,
  getExpiryBaseTimestamp,
  filterLocationsByDeepFreezerRule,
  resetExpiryRulesCache,
  formatDurationFromHours,
  type ExpiryRuleRow,
} from '../services/milk-expiry-rules';
defineOptions({ inheritAttrs: false });

const { pickerDateToUtcIso } = useTimezone();

const toast = useToaster();
const { t, locale } = useLocale();

// ---------------------------------------------------------------------------
// Props & Emits
// ---------------------------------------------------------------------------
interface Props {
  visible: boolean;
  /** Which tab context: 'mother' or 'donor' */
  origin: 'mother' | 'donor';
  /** Provider ID pre-filled when opened from a specific provider's profile/table row. */
  prefilledProviderId?: string;
  /** Provider display name pre-filled when opened from a specific provider's profile/table row. */
  prefilledProviderName?: string;
  /** Provider MRN pre-filled when opened from a specific provider's profile/table row. */
  prefilledProviderMrn?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
  'container-received': [payload: ReceiveMilkContainerPayload];
  /** Emitted when the user wants to switch to the Receive Multiple flow */
  'switch-to-multiple': [];
}>();

// ---------------------------------------------------------------------------
// Dropdown options (i18n-aware via useEnumTranslation)
// ---------------------------------------------------------------------------
const {
  milkStatusOptions,
  getMilkTypeLabel,
  getExpiryLocationLabel,
} = useEnumTranslation();

const settingsStore = useSettingsStore();

// Fallback shelf-life rules used when the API call fails or returns no match
const SHELF_LIFE_RULES: Record<string, number> = {
  FRESH: 48,
  THAWED: 24,
};

// API-fetched expiry offset (in hours) for the currently selected milk status
const apiExpiryOffsetHours = ref<number | null>(null);
const isFetchingExpiryRule = ref(false);
/** true = API returned a matching rule; false = no rule found/configured; null = not yet checked */
const expiryRuleFound = ref<boolean | null>(null);

/**
 * storage_type of the currently selected Location, mapped to the expiry-rule
 * Location bucket ('Fridge' | 'Freezer' | 'Deep Freezer'). Undefined while no
 * location is selected or the selected location has no known storage_type.
 */
const selectedLocationBucket = computed(() => {
  const opt = locationOptions.value.find((o) => o.value === location.value);
  return getLocationBucket(opt?.storageType);
});

/** Hospital's MILK expiry rule rows — used to determine Deep Freezer availability (Settings > Expiry Rules). */
const milkExpiryRuleRows = ref<ExpiryRuleRow[]>([]);

async function loadMilkExpiryRuleRows() {
  try {
    milkExpiryRuleRows.value = await getExpiryRows('MILK');
  } catch {
    milkExpiryRuleRows.value = [];
  }
}

/**
 * Locations offered in the Location select — restricted to Fridge-type
 * storage while milkStatus is THAWED (thawed milk may only be stored in a
 * Fridge), Fridge-type storage EXCLUDED while milkStatus is FROZEN (frozen
 * milk must never be stored in a Fridge/Refrigerator), and with Deep Freezer
 * locations hidden whenever the applicable milk type's (Mother vs Donor)
 * Deep Freezer expiry rule is disabled in Settings > Expiry Rules (see
 * filterLocationsByDeepFreezerRule).
 */
const availableLocationOptions = computed(() => {
  let statusFiltered = locationOptions.value;
  if (milkStatus.value === 'THAWED') {
    statusFiltered = locationOptions.value.filter((o) => getLocationBucket(o.storageType) === 'Fridge');
  } else if (milkStatus.value === 'FROZEN') {
    statusFiltered = locationOptions.value.filter((o) => getLocationBucket(o.storageType) !== 'Fridge');
  }
  return filterLocationsByDeepFreezerRule(
    statusFiltered,
    props.origin === 'donor' ? 'DONOR' : 'MOM',
    milkExpiryRuleRows.value,
  );
});

/**
 * Fetch the matching milk expiry rule from
 * GET /settings/api/v1/milkExpiryRules/?hospital_id=...&expiry_type=MILK
 * whenever the operator selects a Milk Status and/or Location. The rule
 * lookup is Origin + Type + Location — NOT Milk Status alone — so Fresh milk
 * stored in a Freezer resolves to a different (Frozen) rule than Fresh milk
 * stored in a Fridge, even though the status is identical.
 */
async function fetchExpiryRuleForStatus(statusValue: string): Promise<void> {
  if (!config.hospitalId) return;
  // THAWED rules don't depend on the physical location; all other statuses
  // do, so wait for a Location selection before treating "no match" as a
  // real configuration gap rather than an incomplete form.
  if (statusValue !== 'THAWED' && !selectedLocationBucket.value) {
    apiExpiryOffsetHours.value = null;
    expiryRuleFound.value = null;
    expiryDate.value = null;
    return;
  }
  isFetchingExpiryRule.value = true;
  apiExpiryOffsetHours.value = null;
  expiryRuleFound.value = null;
  try {
    const hours = await getExpiryOffsetHoursForStatus(props.origin, statusValue, selectedLocationBucket.value);
    if (hours !== null) {
      apiExpiryOffsetHours.value = hours;
      expiryRuleFound.value = true;
    } else {
      expiryRuleFound.value = false;
      expiryDate.value = null;
    }
  } catch {
    expiryRuleFound.value = false;
    expiryDate.value = null;
  } finally {
    isFetchingExpiryRule.value = false;
  }
}

// ---------------------------------------------------------------------------
// Storage location options (fetched from API)
// ---------------------------------------------------------------------------
const locationOptions = ref<{ label: string; value: string; storageType?: string; storageLocationId?: string }[]>([]);
const isLocationLoading = ref(false);

async function fetchStorageLocations() {
  isLocationLoading.value = true;
  try {
    const api = getApiClient();
    const res = await api.get(`${API_ROUTES.SETTINGS}/storage-locations/`);
    const data = res.data?.data ?? res.data;
    if (Array.isArray(data)) {
      locationOptions.value = data
        .filter((loc: { is_active?: boolean }) => loc.is_active !== false)
        .map((loc: { name: string; storage_location_id: string; storage_type?: string }) => ({
          label: formatLocationLabel(loc.name, getMilkStorageTypeLabel(loc.storage_type)),
          value: loc.name,
          storageType: loc.storage_type,
          storageLocationId: loc.storage_location_id,
        }));
    }
  } catch {
    // Fallback to empty if API fails
    locationOptions.value = [];
  } finally {
    isLocationLoading.value = false;
  }
}

// ---------------------------------------------------------------------------
// Milk type options (from settings store — avoids redundant API calls)
// ---------------------------------------------------------------------------
const milkTypeApiOptions = computed(() =>
  settingsStore.milkTypes
    .filter((mt: MilkTypeEntry) => mt.status === 'Active' && mt.typeCode !== 'OPEN')
    .map((mt: MilkTypeEntry) => ({
      label: getMilkTypeLabel(mt.typeCode) || mt.milkType,
      value: mt.typeCode,
    }))
);
const isMilkTypeLoading = computed(() => settingsStore.milkTypesLoading);


// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------
const containerId = ref('');
const personName = ref('');
const volumeMl = ref<number | null>(null);
const milkType = ref<string | null>(null);
const milkStatus = ref<string | null>(null);
const donorStatus = ref<DonorApprovalStatus | null>(null);
const location = ref('');
const pumpedDate = ref<Date | null>(null);
const expiryDate = ref<Date | null>(null);

const { markDirty, reset: resetDirty, guardClose, confirmState, onConfirm, onCancel, onVisibleUpdate } = useFormDirty();
// Track dirty state — watch all form fields for any user change
const _formValues = computed(() => ({
  containerId: containerId.value,
  personName: personName.value,
  volumeMl: volumeMl.value,
  milkType: milkType.value,
  milkStatus: milkStatus.value,
}));
watch(_formValues, () => markDirty(), { deep: true });

// ---------------------------------------------------------------------------
// Barcode scanner via SSE
// One instance per dialog — shared by all ScanInput fields in this form.
// ---------------------------------------------------------------------------
const scanner = useBarcodeScanner({ workflow: 'receive-milk' });

// Call the API whenever Milk Status and/or Location changes — both are part
// of the rule lookup key (Origin + Type + Location), so changing either one
// must recalculate the expiry date.
watch([milkStatus, location], ([newStatus]: [string | null, string]) => {
  if (newStatus) {
    // Clear stale expiry immediately so no old value lingers while fetch runs
    expiryDate.value = null;
    fetchExpiryRuleForStatus(newStatus);
  } else {
    apiExpiryOffsetHours.value = null;
    expiryRuleFound.value = null;
    expiryDate.value = null;
  }
});

// Switching to THAWED restricts the Location select to Fridge-only options —
// clear an already-selected non-Fridge location (including one with an
// unrecognized/missing storage_type, i.e. an undefined bucket) so it can't
// remain silently invalid. Switching to FROZEN excludes Fridge locations —
// clear an already-selected Fridge location for the same reason.
watch(milkStatus, (newStatus) => {
  if (newStatus === 'THAWED' && location.value && selectedLocationBucket.value !== 'Fridge') {
    location.value = '';
  } else if (newStatus === 'FROZEN' && location.value && selectedLocationBucket.value === 'Fridge') {
    location.value = '';
  }
});

// ---------------------------------------------------------------------------
// Validation errors
// ---------------------------------------------------------------------------
const errors = ref<Record<string, string>>({});
const submitted = ref(false);
const isSubmitting = ref(false);



watch(containerId, () => {
  // Clear stale scan state only when a scan result is present.
  // Typing a new container ID invalidates a previous scan, but must not wipe
  // a provider that was pre-filled from the route (no scan has occurred yet).
  if (scannedOrigin.value !== null) {
    scannedOrigin.value = null;
    scannedProviderId.value = null;
    personName.value = '';
  }
});

// ---------------------------------------------------------------------------
// Scan state — populated by GET /api/v1/milk-providers on Scan click
// ---------------------------------------------------------------------------
const isScanLoading = ref(false);
/** Origin returned by the API after a successful scan; overrides props.origin in the display. */
const scannedOrigin = ref<'mother' | 'donor' | null>(null);
/** person_id from the API response — becomes provider_id in the POST payload. */
const scannedProviderId = ref<string | null>(null);

// ---------------------------------------------------------------------------
// Computed helpers
// ---------------------------------------------------------------------------
const dialogTitle = computed(() => {
  const _lng = locale.value;
  return props.origin === 'mother' ? t('inventory.receiveMotherMilkContainer') : t('inventory.receiveDonorMilkContainer');
});

const personLabel = computed(() => {
  const _lng = locale.value;
  return props.origin === 'mother' ? t('mother.name') : t('donor.name');
});

const personPlaceholder = computed(() => {
  const _lng = locale.value;
  return props.origin === 'mother'
    ? t('inventory.searchMotherInPatientDb')
    : t('inventory.searchDonorInPatientDb');
});

// Current moment used as the upper bound for Pumped Date — refreshed on dialog
// open and whenever the picker panel is shown so users can never select a
// future date *or* a future time later today.
const today = ref(new Date());

function refreshToday() {
  today.value = new Date();
}

/** Human-readable shelf-life hint based on selected status */
const shelfLifeHint = computed(() => {
  const _lng = locale.value;
  // No configured rule for this origin + status + location — the warning box handles the message
  if (expiryRuleFound.value === false) {
    return t('inventory.noExpiryRuleForLocation');
  }
  const hours = apiExpiryOffsetHours.value ?? (milkStatus.value ? SHELF_LIFE_RULES[milkStatus.value] : undefined);
  if (!hours) {
    return t('inventory.shelfLifeUnknown');
  }
  const duration = formatDurationFromHours(hours);
  const locationLabel = selectedLocationBucket.value ? getExpiryLocationLabel(selectedLocationBucket.value) : '';
  let translated: string;
  if (milkStatus.value === 'FRESH') {
    translated = t('inventory.shelfLifeFresh', { hours, duration, location: locationLabel });
  } else if (milkStatus.value === 'FROZEN') {
    translated = t('inventory.shelfLifeFrozen', { hours, duration, location: locationLabel });
  } else if (milkStatus.value === 'PASTEURISED') {
    translated = t('inventory.shelfLifePasteurised', { hours, duration, location: locationLabel });
  } else {
    translated = t('inventory.shelfLifeThawed', { hours, duration });
  }
  // Locale templates wrap {{location}} in parentheses (e.g. "({{location}})");
  // when there's no location label (legacy container option with no storageType),
  // strip the resulting stray "()" instead of showing it to the user.
  if (!locationLabel) {
    translated = translated.replace(/\s*\(\s*\)/g, '');
  }
  // If the locale API serves a stale translation that doesn't reflect the actual
  // API-driven duration, override with a code-built string so the user always
  // sees the correct value from the settings.
  if (!translated.includes(duration)) {
    const locationSuffix = locationLabel ? ` (${locationLabel})` : '';
    if (milkStatus.value === 'FRESH') {
      return `Shelf life: Fresh milk expires in ${duration}${locationSuffix}. Auto-calculated from Pumped Date.`;
    }
    if (milkStatus.value === 'FROZEN') {
      return `Shelf life: Frozen milk expires in ${duration}${locationSuffix}. Auto-calculated from Pumped Date.`;
    }
    if (milkStatus.value === 'PASTEURISED') {
      return `Shelf life: Pasteurised milk expires in ${duration}${locationSuffix}. Auto-calculated from Pumped Date.`;
    }
    return `Shelf life: Thawed milk expires in ${duration}. Auto-calculated from Pumped Date.`;
  }
  return translated;
});

/** Milk status options — same options for both Mother and Donor origins */
const milkStatusOptionsForOrigin = computed(() => milkStatusOptions.value);

/** Shows a warning when the API has no matching expiry rule for the selected status + origin */
const showExpiryWarning = computed(
  () => milkStatus.value !== null && !isFetchingExpiryRule.value && expiryRuleFound.value === false,
);

/** Tracks whether expiry was auto-calculated (true) or manually edited by the user (false). */
const _expiryAutoCalculated = ref(true);

// ---------------------------------------------------------------------------
// Auto-calculate expiry whenever Pumped Date, Milk Status, Location, or the
// API-fetched shelf-life offset changes.
// ---------------------------------------------------------------------------
/**
 * Recomputes Expiry Date from the Receive Milk Expiry Rules base timestamp
 * (Current Timestamp vs Pump Date — see `getExpiryBaseTimestamp`; NEVER the
 * milk batch/container creation timestamp) plus the hospital's configured
 * shelf-life duration. No-ops until both the base timestamp and the API
 * offset are resolvable; a missing/cleared value is nulled out separately by
 * the milkStatus/location watcher above.
 */
function recalcExpiryDate() {
  const baseTimestamp = getExpiryBaseTimestamp({
    milkStatus: milkStatus.value,
    locationType: selectedLocationBucket.value,
    pumpDate: pumpedDate.value,
  });
  if (!baseTimestamp || apiExpiryOffsetHours.value == null) return;
  expiryDate.value = new Date(baseTimestamp.getTime() + apiExpiryOffsetHours.value * 60 * 60 * 1000);
}

watch([pumpedDate, milkStatus, selectedLocationBucket, apiExpiryOffsetHours], () => {
  recalcExpiryDate();
});

// Reset form when dialog opens
watch(
  () => props.visible,
  async (visible: boolean) => {
    if (visible) {
      resetForm();
      fetchStorageLocations();
      loadMilkExpiryRuleRows();
      settingsStore.ensureMilkTypes();
      nextTick(() => resetDirty());
      // Connect scanner SSE stream when dialog becomes visible
      await scanner.connectScanner();
    } else {
      // Tear down scanner SSE stream when dialog closes
      scanner.disconnectScanner();
      resetExpiryRulesCache();
    }
  },
  { immediate: true },
);

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------
function resetForm() {
  refreshToday();
  containerId.value = '';
  // Seed provider details from route-passed prefill so that opening from a
  // specific mother/donor profile submits the correct providerId without
  // requiring a manual scan.
  personName.value = props.prefilledProviderName ?? '';
  volumeMl.value = null;
  milkType.value = null;
  milkStatus.value = null;
  donorStatus.value = null;
  location.value = '';
  pumpedDate.value = null;
  expiryDate.value = null;
  errors.value = {};
  submitted.value = false;
  isScanLoading.value = false;
  scannedOrigin.value = null;
  scannedProviderId.value = props.prefilledProviderId ?? null;
  expiryRuleFound.value = null;
  apiExpiryOffsetHours.value = null;
}

async function handleScan() {
  const customLabel = containerId.value.trim();
  if (!customLabel) {
    toast.danger(t('inventory.enterLabelBeforeScanning'));
    return;
  }
  isScanLoading.value = true;
  scannedOrigin.value = null;
  scannedProviderId.value = null;
  personName.value = '';
  try {
    const provider = await getMilkProviderByLabel(customLabel);

    const isBothProvider = provider.person_type === 'BOTH';
    const apiOrigin = provider.person_type === 'MOTHER' ? 'mother' : 'donor';

    // Mismatch check: scanned provider belongs to the wrong origin type for this tab.
    // BOTH providers can be received on either the Mother or Donor tab — no mismatch.
    if (!isBothProvider && apiOrigin !== props.origin) {
      const found = apiOrigin === 'mother' ? t('common.mother') : t('common.donor');
      const expected = found;
      toast.danger(t('inventory.providerMismatch', { found, expected }));
      scannedProviderId.value = null;
      personName.value = '';
      return;
    }

    // Populate Origin Type — for BOTH providers use the current tab's origin
    const effectiveOrigin = isBothProvider ? props.origin : apiOrigin;
    scannedOrigin.value = effectiveOrigin;

    // Donor screening check: only APPROVED donors may have milk received.
    if (effectiveOrigin === 'donor' && provider.screening_status !== 'APPROVED') {
      toast.danger(t('inventory.donorScreeningNotApproved'));
      scannedOrigin.value = null;
      scannedProviderId.value = null;
      personName.value = `${provider.first_name} ${provider.last_name}`;
      return;
    }
    scannedProviderId.value = provider.person_id;
    // Populate Mother / Donor Name
    personName.value = `${provider.first_name} ${provider.last_name}`;
    // Clear stale name error if present
    if (errors.value.personName) {
      const { personName: _removed, ...rest } = errors.value;
      errors.value = rest;
    }
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) {
      toast.danger(t('inventory.noProviderFound'));
    } else {
      toast.danger(t('inventory.failedLookupProvider'));
    }
  } finally {
    isScanLoading.value = false;
  }
}

function validate(): boolean {
  const errs: Record<string, string> = {};

  // Container ID
  const cidTrimmed = containerId.value.trim();
  if (!cidTrimmed) {
    errs.containerId = t('inventory.containerIdRequired');
  } else if (!/^[A-Za-z0-9\-]+$/.test(cidTrimmed)) {
    errs.containerId = t('inventory.containerIdAlphanumeric');
  }

  // Person name
  if (!personName.value.trim()) {
    errs.personName = t('inventory.personNameRequired', { label: personLabel.value });
  }

  // Volume
  if (volumeMl.value === null || volumeMl.value <= 0) {
    errs.volumeMl = t('inventory.volumePositive');
  }

  // Milk type
  if (!milkType.value) {
    errs.milkType = t('inventory.milkTypeRequired');
  }

  // Milk status
  if (!milkStatus.value) {
    errs.milkStatus = t('inventory.milkStatusRequired');
  }

  // Donor status (required when origin is donor)

  // Location
  if (!location.value) {
    errs.location = t('inventory.locationRequired');
  } else if (milkStatus.value === 'THAWED' && selectedLocationBucket.value !== 'Fridge') {
    // Last-line-of-defense: catches an incompatible location that reached
    // the model through any path other than the milkStatus watcher above.
    errs.location = t('inventory.thawedLocationMustBeFridge');
  } else if (milkStatus.value === 'FROZEN' && selectedLocationBucket.value === 'Fridge') {
    errs.location = t('inventory.frozenLocationCannotBeFridge');
  }

  // Pumped Date
  if (!pumpedDate.value) {
    errs.pumpedDate = t('inventory.pumpedDateRequired');
  } else if (pumpedDate.value > today.value) {
    errs.pumpedDate = t('inventory.pumpedDateFuture');
  }

  // Expiry Date
  if (!expiryDate.value) {
    errs.expiryDate = t('inventory.expiryDateRequired');
  } else if (pumpedDate.value && expiryDate.value <= pumpedDate.value) {
    errs.expiryDate = t('inventory.expiryDateAfterPumped');
  }

  errors.value = errs;
  return Object.keys(errs).length === 0;
}

async function handleConfirm() {
  submitted.value = true;

  // Explicit 0/negative check (surfaced as a toast) — kept separate from the
  // general field-level `validate()` below so the user gets a clear, specific
  // reason a 0 volume can't be saved, rather than a generic form-error state.
  if (volumeMl.value !== null && volumeMl.value <= 0) {
    toast.warn(t('inventory.volumePositive'));
    return;
  }

  if (!validate()) return;

  isSubmitting.value = true;
  try {
    const resolvedStorageLocationId = locationOptions.value.find((o) => o.value === location.value)?.storageLocationId;
    const payload: ReceiveMilkContainerPayload =
      props.origin === 'mother'
        ? {
            origin: 'mother',
            containerId: containerId.value.trim(),
            motherName: personName.value.trim(),
            providerId: scannedProviderId.value ?? props.prefilledProviderId ?? undefined,
            volumeMl: volumeMl.value!,
            milkType: milkType.value! as MilkReceivableMilkType,
            status: milkStatus.value! as MilkReceivableMilkStatus,
            location: location.value,
            storageLocationId: resolvedStorageLocationId,
            pumpedDate: pickerDateToUtcIso(pumpedDate.value!) ?? pumpedDate.value!.toISOString(),
            expiryDate: pickerDateToUtcIso(expiryDate.value!) ?? expiryDate.value!.toISOString(),
          }
        : {
            origin: 'donor',
            containerId: containerId.value.trim(),
            donorName: personName.value.trim(),
            providerId: scannedProviderId.value ?? props.prefilledProviderId ?? undefined,
            volumeMl: volumeMl.value!,
            milkType: milkType.value! as MilkReceivableMilkType,
            status: milkStatus.value! as MilkReceivableMilkStatus,
            donorStatus: donorStatus.value ?? 'Pending',
            location: location.value,
            storageLocationId: resolvedStorageLocationId,
            pumpedDate: pickerDateToUtcIso(pumpedDate.value!) ?? pumpedDate.value!.toISOString(),
            expiryDate: pickerDateToUtcIso(expiryDate.value!) ?? expiryDate.value!.toISOString(),
          };

    emit('container-received', payload);
    resetDirty();
    emit('update:visible', false);
  } finally {
    isSubmitting.value = false;
  }
}

async function handleCancel() {
  if (!(await guardClose())) return;
  resetDirty();
  resetForm();
  scanner.disconnectScanner();
  emit('update:visible', false);
}

async function handleClose() {
  if (!(await guardClose())) return;
  resetDirty();
  resetForm();
  scanner.disconnectScanner();
  emit('update:visible', false);
}

async function handleSwitchToMultiple() {
  if (!(await guardClose())) return;
  resetDirty();
  resetForm();
  scanner.disconnectScanner();
  emit('update:visible', false);
  emit('switch-to-multiple');
}
</script>

<template>
  <DialogModal
    :visible="visible"
    data-testid="receive-milk-dialog-modal"
    :title="dialogTitle"
    max-width="max-w-2xl"
    position="center"
    :show-close="true"
    intercept-close
    @update:visible="(v: boolean) => { if (!v) handleClose(); }"
    @close="handleClose"
  >
    <template #header>
      <div class="flex flex-1 items-center justify-between mr-3 min-w-0">
        <span class="text-lg font-medium text-gray-900 not-italic">{{ dialogTitle }}</span>
        <Button
          severity="primary"
          outlined
          size="small"
          class="shrink-0 flex items-center gap-1.5"
          data-testid="switch-to-multiple-btn"
          @click="handleSwitchToMultiple"
        >
          <Icon
            name="DropletBottleAlt"
            size-class="h-4 w-4"
          />
          {{ $t('inventory.receiveMultiple') }}
        </Button>
      </div>
    </template>
    <div class="flex flex-col gap-5 px-1 py-2">
      <!-- Container ID -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">
          {{ $t('inventory.containerId') }}
          <span class="text-danger ml-0.5">*</span>
          <span class="text-gray-600 font-normal text-xs ml-1">{{ $t('inventory.scanBarcodeOrEnterManually') }}</span>
        </label>
        <div class="flex gap-2">
          <!-- ScanInput: inline scan button + per-field status chip -->
          <ScanInput
            v-model="containerId"
            :scanner="scanner"
            data-testid="container-id-input"
            :placeholder="$t('inventory.scanOrTypeId')"
            :maxlength="50"
            :invalid="!!(submitted && errors.containerId)"
            wrapper-class="flex-1"
            @scan="handleScan"
          />
          <!-- Provider lookup button (looks up donor/mother by the typed/scanned ID) -->
          <Button
            :label="$t('common.search')"
            data-testid="scan-btn"
            severity="secondary"
            outlined
            class="shrink-0"
            :loading="isScanLoading"
            @click="handleScan"
          />
        </div>
        <!-- Scanner setup error (no device / no tenant) -->
        <small
          v-if="scanner.scannerError.value"
          class="text-yellow-700 text-xs"
        >
          {{ scanner.scannerError.value }}
        </small>
        <small
          v-if="submitted && errors.containerId"
          class="text-danger text-xs"
        >
          {{ errors.containerId }}
        </small>
      </div>

      <!-- Origin Type + Person Name -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700">
            {{ $t('inventory.originType') }}
          </label>
          <div class="flex items-center h-10 px-3 rounded-md border border-gray-300 bg-gray-50 text-sm text-gray-700 capitalize select-none">
            {{ (scannedOrigin ?? props.origin) === 'mother' ? t('common.mother') : (scannedOrigin ?? props.origin) === 'donor' ? t('common.donor') : (scannedOrigin ?? props.origin) }}
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700">
            {{ personLabel }}
            <span class="text-danger ml-0.5">*</span>
          </label>
          <InputText
            v-model="personName"
            data-testid="person-name-input"
            :placeholder="personPlaceholder"
            class="w-full"
            :class="{ 'p-invalid': submitted && errors.personName }"
            :readonly="!!scannedProviderId"
          />
          <small
            v-if="submitted && errors.personName"
            class="text-danger text-xs"
          >
            {{ errors.personName }}
          </small>
          <small
            v-if="props.prefilledProviderMrn && scannedOrigin === null"
            data-testid="prefilled-mrn-hint"
            class="text-gray-600 text-xs"
          >
            {{ $t('common.mrn') }}: {{ props.prefilledProviderMrn }}
          </small>
        </div>
      </div>

      <!-- Volume + Milk Type -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700">
            {{ $t('feed.volume') }}
            <span class="text-danger ml-0.5">*</span>
          </label>
          <InputNumber
            v-model="volumeMl"
            data-testid="volume-input"
            :placeholder="$t('common.milkUnit')"
            :max="9999"
            :use-grouping="false"
            class="w-full"
            :input-class="submitted && errors.volumeMl ? 'p-invalid w-full' : 'w-full'"
          />
          <small
            v-if="submitted && errors.volumeMl"
            class="text-danger text-xs"
          >
            {{ errors.volumeMl }}
          </small>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700">
            {{ $t('feed.milkType') }}
            <span class="text-danger ml-0.5">*</span>
          </label>
          <Select
            v-model="milkType"
            data-testid="milk-type-select"
            :options="milkTypeApiOptions"
            option-label="label"
            option-value="value"
            :placeholder="$t('common.select')"
            :loading="isMilkTypeLoading"
            class="w-full"
            :class="{ 'p-invalid': submitted && errors.milkType }"
          />
          <small
            v-if="submitted && errors.milkType"
            class="text-danger text-xs"
          >
            {{ errors.milkType }}
          </small>
        </div>
      </div>

      <!-- Milk Status + Location -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700">
            {{ $t('inventory.milkStatus') }}
            <span class="text-danger ml-0.5">*</span>
          </label>
          <Select
            v-model="milkStatus"
            data-testid="milk-status-select"
            :options="milkStatusOptionsForOrigin"
            option-label="label"
            option-value="value"
            :placeholder="$t('common.select')"
            class="w-full"
            :class="{ 'p-invalid': submitted && errors.milkStatus }"
          />
          <small
            v-if="submitted && errors.milkStatus"
            class="text-danger text-xs"
          >
            {{ errors.milkStatus }}
          </small>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700">
            {{ $t('common.location') }}
            <span class="text-danger ml-0.5">*</span>
          </label>
          <Select
            v-model="location"
            data-testid="location-select"
            :options="availableLocationOptions"
            option-label="label"
            option-value="value"
            :placeholder="$t('common.select')"
            :loading="isLocationLoading"
            class="w-full"
            :class="{ 'p-invalid': submitted && errors.location }"
          />
          <small
            v-if="submitted && errors.location"
            class="text-danger text-xs"
          >
            {{ errors.location }}
          </small>
        </div>
      </div>



      <!-- Pumped Date + Expiry Date -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700">
            {{ $t('inventory.pumpedDate') }}
            <span class="text-danger ml-0.5">*</span>
          </label>
          <AppDatePicker
            v-model="pumpedDate"
            data-testid="pumped-date-picker"
            :placeholder="$t('inventory.datePlaceholderDmyHm')"
            date-format="dd/mm/yy"
            :max-date="today"
            show-icon
            show-time
            hour-format="24"
            class="w-full"
            :class="{ 'p-invalid': submitted && errors.pumpedDate }"
            @show="refreshToday"
          />
          <small
            v-if="submitted && errors.pumpedDate"
            class="text-danger text-xs"
          >
            {{ errors.pumpedDate }}
          </small>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700">
            {{ $t('milk.expiryDate') }}
            <span class="text-danger ml-0.5">*</span>
          </label>
          <AppDatePicker
            v-model="expiryDate"
            data-testid="expiry-date-picker"
            :placeholder="$t('inventory.datePlaceholderDmyHm')"
            date-format="dd/mm/yy"
            show-icon
            show-time
            hour-format="24"
            disabled
            class="w-full"
            :class="{ 'p-invalid': submitted && errors.expiryDate }"
          />
          <small
            v-if="submitted && errors.expiryDate"
            class="text-danger text-xs"
          >
            {{ errors.expiryDate }}
          </small>
        </div>
      </div>

      <!-- Expiry settings warning -->
      <div
        v-if="showExpiryWarning"
        class="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-300 px-4 py-3 text-xs text-amber-700"
      >
        <Icon
          name="CircleExclamation"
          size-class="h-4 w-4 shrink-0 mt-0.5"
          color="text-amber-600"
        />
        <span>{{ $t('inventory.expirySettingsNotConfigured') }}</span>
      </div>

      <!-- Shelf-life info banner — only shown once both Milk Status and Pumped Date are selected -->
      <div
        v-if="milkStatus && pumpedDate"
        class="flex items-start gap-2 rounded-md bg-primary-50 border border-primary-200 px-4 py-3 text-xs text-primary-700"
      >
        <Icon
          name="InfoCircle"
          size-class="h-4 w-4 shrink-0 mt-0.5"
          color="text-blue-900"
        />
        <span>{{ shelfLifeHint }}</span>
      </div>
    </div>

    <!-- Footer -->
    <template #footer>
      <div class="w-full flex justify-start gap-3">
        <Button
          data-testid="confirm-receipt-btn"
          :label="$t('inventory.confirmReceipt')"
          severity="primary"
          :loading="isSubmitting"
          @click="handleConfirm"
        />
        <Button
          :label="$t('common.cancel')"
          data-testid="cancel-receipt-btn"
          severity="secondary"
          outlined
          :disabled="isSubmitting"
          @click="handleCancel"
        />
      </div>
    </template>
  </DialogModal>

  <FormDirtyDialog
    :visible="confirmState.visible"
    @confirm="onConfirm"
    @cancel="onCancel"
    @update:visible="onVisibleUpdate"
  />
</template>
